<template>
  <LoadingSpinner v-if="$apollo.loading" />
  <div v-else>
    <h3>
      <span class="parent" v-if="concept.depth > 2">
        <a href="#" @click.stop="$emit('select-concept-uri', parent.uri)">{{
          conceptLabel(parent)
        }}</a>
        --
      </span>

      {{ conceptLabel(concept) }}
    </h3>
    <p class="description-html" v-if="descriptionHtml" v-html="descriptionHtml" />
    <div class="children" v-if="children.length > 0">
      <em>Children:</em>
      <ul>
        <li v-for="child in children" :key="child.uri">
          <a href="#" @click.stop="$emit('select-concept-uri', child.uri)">{{
            conceptLabel(child)
          }}</a>
        </li>
      </ul>
    </div>
    <div class="footer" v-if="concept.depth > 1">
      <div class="attribution">
        From
        <a href="#" @click.stop="$emit('select-concept-uri', firstAncestor.uri)">{{
          firstAncestor.label
        }}</a>
      </div>
      <CopyButton
        v-if="SHOW_CONCEPT_COPY_BUTTON"
        style="text-align: left"
        :text="concept.uri"
        @copy-button-click="copyConcept(concept)"
      />
    </div>
    <button
      v-if="hasMore"
      @click.stop="$emit('select-concept-uri', null)"
      title="Show all concepts for current selection"
    >
      Show all concepts
    </button>
  </div>
</template>

<script>
  import gql from 'graphql-tag';
  import LoadingSpinner from '@/symphony/widgets/components/LoadingSpinner.vue';
  import CopyButton from '@/symphony/widgets/components/CopyButton.vue';
  import { CONCEPTS_ARE_LN, SHOW_CONCEPT_COPY_BUTTON } from '@/store/constants';

  export default {
    name: 'ConceptDetail',
    components: {
      LoadingSpinner,
      CopyButton,
    },
    props: {
      conceptUri: {
        type: String,
      },
      hasMore: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['select-concept-uri'],
    computed: {
      // NOTE: Sorting is encoded in the ConceptualNode hierarchy
      children() {
        return this.concept.children;
      },
      parent() {
        return this.concept.parent;
      },
      descriptionHtml() {
        return this.concept.data?.description;
      },
      firstAncestor() {
        return this.concept.ancestors[0];
      },
    },
    methods: {
      conceptLabel(concept) {
        const label = concept.label || '<Unknown>';
        if (CONCEPTS_ARE_LN) {
          return `${label} (${concept.data?.code})`;
        }
        return label;
      },
      // TODO: Refactor as a composable
      copyConcept(concept) {
        const { uri } = concept;
        const copyPayload = uri;
        navigator.clipboard.writeText(copyPayload).then(
          () => {
            /* clipboard successfully set */
            const msg = `Copied URI to clipboard: ${copyPayload}`;
            console.log(`Success: ${msg}`);
            // eslint-disable-next-line no-alert
            alert(msg);
          },
          () => {
            /* clipboard write failed */
            console.error('Failed to copy URI to clipboard.');
          },
        );
      },
    },
    apollo: {
      concept: {
        // FIXME: data for LN
        query: gql`
          query Concepts($filters: ConceptualNodeFilter) {
            conceptualNodes(filters: $filters) {
              id
              depth
              label
              uri
              data
              ancestors {
                uri
                data
                label
              }
              parent {
                uri
                label
                data
                depth
              }
              children {
                uri
                data
                label
              }
            }
          }
        `,
        variables() {
          return {
            filters: {
              uri: {
                exact: this.conceptUri,
              },
            },
          };
        },
        skip() {
          return !this.conceptUri;
        },
        update(data) {
          return data.conceptualNodes[0];
        },
      },
    },
    created() {
      this.SHOW_CONCEPT_COPY_BUTTON = SHOW_CONCEPT_COPY_BUTTON;
    },
  };
</script>

<!-- TODO: Refactor with Text Structure Widget -->
<style scoped lang="scss">
  .children {
    ul {
      margin-block-start: 0.5em;
      > li {
        margin-block-end: 0.25em;
      }
    }
  }
  .parent {
    margin-block-end: 1em;
  }
  .footer {
    display: flex;
    justify-content: space-between;
    .attribution {
      font-style: italic;
    }
  }
  a {
    user-select: none;
    cursor: pointer;
    color: unset;
    &:hover {
      color: #4d4ddd;
    }
  }
  .description-html {
    :deep(.caller) {
      display: none;
    }
    :deep(.gle) {
      color: green;
    }
    :deep(.com) {
      margin-top: 1em;
      display: inline-block;
      color: gray;
    }
  }
</style>
